import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  RadialLinearScale,
} from 'chart.js'

import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import {
  fetchActionsDistribution,
  getRisquebyId,
} from '../../redux/risques/risqueActions'
import { selectActionsDistribution } from '../../redux/risques/risqueSelectors'
import LineChart from '../../components/common/LineChart'
import { hasChartData } from '../../components/common/PieChart'
import {
  AnalysisChartCard,
  AnalysisPageLoader,
  AnalysisSection,
} from '../../components/common/analysisLayout'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import AnalysisPdfReport from '../../Pdf/AnalysisPdfReport'
import '../../components/common/maturity-analysis.css'

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale
)

const RiskIndicatorPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch: AppDispatch = useAppDispatch()
  const risque = useSelector((state: RootState) => state.risque.risque)
  const actionsDistribution = useSelector(selectActionsDistribution)
  const pdf = useAnalysisPdfExport()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!id) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await Promise.all([
          dispatch(getRisquebyId(id)),
          dispatch(fetchActionsDistribution(id)),
        ])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [dispatch, id])

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  const chartData = actionsDistribution?.distribution
    ? {
        labels: Object.keys(actionsDistribution.distribution),
        datasets: [
          {
            label: t('charts.actionsByControl.datasetLabel'),
            data: Object.values(actionsDistribution.distribution),
            borderColor: '#0D9B87',
            backgroundColor: 'rgba(13, 155, 135, 0.18)',
          },
        ],
      }
    : { labels: [], datasets: [] }

  const backPath = id ? `/risque-detail/${id}` : '/risques'

  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('analysis.riskAnalysis.eyebrow', {
          defaultValue: 'Risk · Detail',
        })}
        title={t('analysis.riskAnalysis.pageTitle', {
          defaultValue: 'Risk Analysis',
        })}
        subtitle={t('analysis.riskAnalysis.subtitle', {
          name: risque?.RisqueName || '—',
          date: computedOn,
          defaultValue: `Indicators for ${risque?.RisqueName || 'this risk'}, computed on ${computedOn}.`,
        })}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('analysis.riskAnalysis.backToRisk', {
                defaultValue: 'Back to risk',
              })}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() =>
                pdf.exportPdf(risque?.RisqueName || 'risk-analysis')
              }
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={1} chartCount={1} />
      ) : (
        <AnalysisSection
          label={t('analysis.riskAnalysis.sectionActions', {
            defaultValue: 'Actions',
          })}
        >
          <AnalysisChartCard
            title={t('charts.actionsByControl.title')}
            hasData={hasChartData(chartData)}
          >
            <LineChart
              embedded
              title={t('charts.actionsByControl.title')}
              data={chartData}
            />
          </AnalysisChartCard>
        </AnalysisSection>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('analysis.riskAnalysis.eyebrow')}
          title={risque?.RisqueName || '—'}
          subtitle={t('analysis.riskAnalysis.subtitle', {
            name: risque?.RisqueName || '—',
            date: computedOn,
          })}
          footerTitle={t('analysis.riskAnalysis.pageTitle')}
          computedOn={computedOn}
          sections={[
            {
              label: t('analysis.riskAnalysis.sectionActions'),
              charts: [
                {
                  key: 'actions',
                  title: t('charts.actionsByControl.title'),
                  kind: 'line',
                  data: chartData,
                },
              ],
            },
          ]}
        />
      </div>
    </div>
  )
}

export default RiskIndicatorPage
